import {
  batch,
  createEffect,
  createMemo,
  createSignal,
  For,
  type JSX,
  Match,
  onCleanup,
  onMount,
  Show,
  Switch,
} from "solid-js";
import { calculerOrdres, TRIS, type Tri } from "../domaine/analyses";
import {
  definirFacettes,
  filtrer,
  indexer,
  pointsCommuns,
  selectionVersTexte,
  texteVersSelection,
  type IdFacette,
  type Mode,
  type Selection,
} from "../domaine/facettes";
import { filtrerProjets, FILTRES_VIDES } from "../domaine/filtres";
import {
  aretes,
  estPlacement,
  PLACEMENT_PAR_DEFAUT,
  grappes,
  plageDeSeuil,
  seuilPourDensite,
  type Placement,
} from "../domaine/graphe";
import { aujourdhuiIso, etatTemporel } from "../domaine/portefeuille";
import { calculerSimilarites, POIDS_PAR_DEFAUT, type Poids } from "../domaine/similarite";
import type { EtatTemporel, Portefeuille, Projet } from "../domaine/types";
import { formaterScore, pluriel } from "../format";
import { ecrireDansAdresse, lireDansAdresse } from "../navigation";
import {
  couleurClasse,
  ongletsPour,
  surDonnee,
  vueDepuisAdresse,
  type Analyse,
  type IdVue,
  VUES,
} from "./analyse";
import { Entete } from "./Entete";
import { creerRegistreFigures, MenuExport } from "./Figure";
import { nombreCriteres, VoletFiltres } from "./Filtres";
import { GrapheSimilarite } from "./GrapheSimilarite";
import { Icone } from "./Icone";
import { Legende } from "./Legende";
import { Panneau, TableauRapprochements } from "./Panneau";
import { VueArbres } from "./VueArbres";
import { VueCroisements } from "./VueCroisements";
import { VueChronologie, VueExploration } from "./VueExploration";
import { VueAccueil } from "./VueAccueil";
import { VueAvis } from "./VueAvis";
import { VueCouverture } from "./VueCouverture";
import { VueLeviers, VueSimilarite } from "./VueMatrices";
import { etatsPresents, LIBELLES_ETAT_FILTRE, VueParametres } from "./VueParametres";

const TOUS_LES_ETATS: EtatTemporel[] = ["encours", "avenir", "termine", "inconnu"];
const DIMENSIONS = ["leviers", "cultures", "bioagresseurs"] as const;
const estTri = (v: string | null): v is Tri => TRIS.some((t) => t.id === v);

// --- état dans l'adresse ------------------------------------------------------

/** `poids=47-29-24` : centièmes bruts, dans l'ordre leviers, cultures, bioagresseurs. */
function lirePoids(): Poids {
  const parts = (lireDansAdresse("poids") ?? "").split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n) || n < 0 || n > 100)) {
    return { ...POIDS_PAR_DEFAUT };
  }
  const [leviers = 0, cultures = 0, bioagresseurs = 0] = parts.map((n) => n / 100);
  return { leviers, cultures, bioagresseurs };
}
const ecrirePoids = (p: Poids) => DIMENSIONS.map((d) => Math.round(p[d] * 100)).join("-");
const poidsEgaux = (a: Poids, b: Poids) => DIMENSIONS.every((d) => Math.abs(a[d] - b[d]) < 0.005);

function lireEtats(): Set<EtatTemporel> {
  const lus = (lireDansAdresse("etats") ?? "")
    .split(",")
    .filter((e): e is EtatTemporel => (TOUS_LES_ETATS as string[]).includes(e));
  return new Set(lus.length ? lus : TOUS_LES_ETATS);
}

/**
 * L'application chargée, disposée comme le POC — trois rangées, trois natures :
 *
 *   en-tête   ce qu'on regarde (les compteurs) et la préférence globale du thème
 *   onglets   où l'on est
 *   barre     ce qu'on règle dans la vue courante, et rien d'autre
 *
 * puis la vue, pleine largeur ; la fiche d'un projet s'ouvre en volet par-dessus.
 */
export function Atlas(props: { portefeuille: Portefeuille }) {
  const aujourdhui = aujourdhuiIso();

  // --- modèle : avancement retenu, filtres, pondération, seuil ---------------
  const [etatsInclus, setEtatsInclus] = createSignal<ReadonlySet<EtatTemporel>>(lireEtats());
  const presents = createMemo(() => etatsPresents(props.portefeuille, aujourdhui));
  const tousInclus = () => presents().every((e) => etatsInclus().has(e.etat));

  // Les filtres à facettes s'appliquent après l'avancement : leurs effectifs se
  // comptent parmi les projets que les Paramètres gardent.
  const facettes = createMemo(() => definirFacettes(props.portefeuille, aujourdhui));
  const [filtres, setFiltres] = createSignal<Selection>(
    texteVersSelection(lireDansAdresse("filtres")),
  );
  const nbFiltres = () => nombreCriteres(filtres());
  const avancementRetenu = (etats: ReadonlySet<EtatTemporel>) =>
    props.portefeuille.projets.filter((p) => etats.has(etatTemporel(p, aujourdhui)));
  const base = createMemo(() =>
    tousInclus() ? props.portefeuille.projets : avancementRetenu(etatsInclus()),
  );
  const index = createMemo(() => indexer(base(), facettes()));
  /** Indices dans `base()` des projets que les filtres retiennent. */
  const retenus = createMemo(() => filtrer(index(), filtres()));
  /** Les projets retenus par un avancement et des filtres donnés, pour un seuil à venir. */
  const projetsRetenus = (etats: ReadonlySet<EtatTemporel>, s: Selection): Projet[] => {
    const parAvancement = avancementRetenu(etats);
    if (!nombreCriteres(s)) return parAvancement;
    return filtrer(indexer(parAvancement, facettes()), s).flatMap((i) => {
      const p = parAvancement[i];
      return p ? [p] : [];
    });
  };
  const portefeuille = createMemo<Portefeuille>(() => {
    if (tousInclus() && !nbFiltres()) return props.portefeuille;
    const projets = base();
    return {
      ...props.portefeuille,
      projets: nbFiltres() ? retenus().flatMap((i) => (projets[i] ? [projets[i]] : [])) : projets,
    };
  });
  const projets = () => portefeuille().projets;

  const [poidsBruts, setPoidsBruts] = createSignal<Poids>(lirePoids());
  const similarites = createMemo(() =>
    calculerSimilarites(projets(), portefeuille().dimensions, poidsBruts()),
  );
  const plage = createMemo(() => plageDeSeuil(similarites()));
  const borner = (s: number) => Math.min(plage().max, Math.max(plage().min, s));
  const seuilLu = Number(lireDansAdresse("seuil"));
  const [seuil, setSeuil] = createSignal(
    Number.isFinite(seuilLu) && seuilLu > 0 ? borner(seuilLu) : plage().defaut,
  );
  const placementLu = lireDansAdresse("placement");
  const [placement, setPlacement] = createSignal<Placement>(
    estPlacement(placementLu) ? placementLu : PLACEMENT_PAR_DEFAUT,
  );
  const changerPlacement = (p: Placement) => {
    setPlacement(p);
    ecrireDansAdresse("placement", p === PLACEMENT_PAR_DEFAUT ? null : p);
  };
  // La sériation dépend de la pondération : recalculée avec elle, pas à chaque rendu.
  const ordres = createMemo(() => calculerOrdres(projets(), portefeuille().leviers, similarites()));
  const liens = createMemo(() => aretes(similarites(), seuil()));

  const ecrireModele = () => {
    ecrireDansAdresse(
      "poids",
      poidsEgaux(poidsBruts(), POIDS_PAR_DEFAUT) ? null : ecrirePoids(poidsBruts()),
    );
    ecrireDansAdresse("etats", tousInclus() ? null : [...etatsInclus()].join(","));
    ecrireDansAdresse("filtres", selectionVersTexte(filtres()) || null);
    ecrireDansAdresse(
      "seuil",
      Math.abs(seuil() - plage().defaut) < 1e-9 ? null : seuil().toFixed(2),
    );
  };

  /**
   * Nouvelle pondération **à densité constante** : sinon elle changerait à la fois
   * quels projets sont reliés et combien de liens il y a, et deux réglages ne se
   * compareraient plus à l'œil.
   */
  const changerPoids = (poids: Poids) => {
    const n = projets().length;
    const densite = n ? liens().length / n : 1.5;
    const nouvelles = calculerSimilarites(projets(), portefeuille().dimensions, poids);
    const p = plageDeSeuil(nouvelles);
    const s = seuilPourDensite(nouvelles, densite);
    batch(() => {
      setPoidsBruts(poids);
      setSeuil(Math.min(p.max, Math.max(p.min, s)));
    });
    ecrireModele();
  };
  /**
   * Change les projets retenus **à densité constante**, comme la pondération : le
   * graphe garde le nombre moyen de liens par projet qu'on avait réglé, et le
   * seuil se déduit de la distribution des scores des projets gardés.
   */
  const changerPerimetre = (etats: ReadonlySet<EtatTemporel>, s: Selection) => {
    const gardes = projetsRetenus(etats, s);
    const n = projets().length;
    const densite = n ? liens().length / n : 1.5;
    const nouvelles = calculerSimilarites(gardes, props.portefeuille.dimensions, poidsBruts());
    const p = plageDeSeuil(nouvelles);
    const seuilGarde = Math.min(p.max, Math.max(p.min, seuilPourDensite(nouvelles, densite)));
    batch(() => {
      setEtatsInclus(etats);
      setFiltres(s);
      setSeuil(seuilGarde);
      if (!gardes.some((p) => p.ref === selection())) setSelection(null);
    });
    ecrireModele();
    ecrireDansAdresse("projet", selection());
  };
  const changerEtats = (etats: ReadonlySet<EtatTemporel>) => changerPerimetre(etats, filtres());
  const changerFiltres = (s: Selection) => changerPerimetre(etatsInclus(), s);
  const basculerFiltre = (id: IdFacette, cle: string) => {
    const c = filtres()[id] ?? { valeurs: [], mode: "un" as Mode };
    const valeurs = c.valeurs.includes(cle)
      ? c.valeurs.filter((v) => v !== cle)
      : [...c.valeurs, cle];
    changerFiltres({ ...filtres(), [id]: { ...c, valeurs } });
  };
  const changerModeFiltre = (id: IdFacette, mode: Mode) => {
    const c = filtres()[id];
    if (c) changerFiltres({ ...filtres(), [id]: { ...c, mode } });
  };
  const libelleFacette = (id: IdFacette) => facettes().find((f) => f.id === id)?.libelle ?? id;
  const communs = createMemo(() => pointsCommuns(index(), retenus(), filtres()));
  const [voletFiltres, setVoletFiltres] = createSignal(false);
  const reinitialiserModele = () => {
    const defaut = plageDeSeuil(
      calculerSimilarites(props.portefeuille.projets, props.portefeuille.dimensions),
    ).defaut;
    batch(() => {
      setPoidsBruts({ ...POIDS_PAR_DEFAUT });
      setEtatsInclus(new Set(TOUS_LES_ETATS));
      setSeuil(defaut);
    });
    changerPlacement(PLACEMENT_PAR_DEFAUT);
    ecrireModele();
  };
  const changerSeuil = (s: number) => {
    setSeuil(borner(s));
    ecrireModele();
  };

  // --- lecture : vue, tri, recherche, sélection ------------------------------
  const [vue, setVue] = createSignal<IdVue>(
    vueDepuisAdresse(lireDansAdresse("vue"), lireDansAdresse("projet")),
  );
  const allerA = (v: IdVue) => {
    setVue(v);
    ecrireDansAdresse("vue", v === "accueil" ? null : v);
  };
  const triLu = lireDansAdresse("tri");
  const [tri, setTri] = createSignal<Tri>(estTri(triLu) ? triLu : "seriation");
  const changerTri = (t: Tri) => {
    setTri(t);
    ecrireDansAdresse("tri", t === "seriation" ? null : t);
  };

  const [recherche, setRecherche] = createSignal(lireDansAdresse("q") ?? "");
  const changerRecherche = (q: string) => {
    setRecherche(q);
    ecrireDansAdresse("q", q.trim() || null);
  };
  // Une recherche met en avant, elle ne retire rien : les effectifs restent justes.
  const misEnAvant = createMemo(() => {
    if (!recherche().trim()) return null;
    const trouves = new Set(
      filtrerProjets(projets(), { ...FILTRES_VIDES, recherche: recherche() }, aujourdhui).map(
        (p) => p.ref,
      ),
    );
    return new Set(projets().flatMap((p, i) => (trouves.has(p.ref) ? [i] : [])));
  });

  const refLue = lireDansAdresse("projet");
  const [selection, setSelection] = createSignal<string | null>(
    projets().some((p) => p.ref === refLue) ? refLue : null,
  );
  const selectionner = (ref: string | null) => {
    setSelection(ref);
    ecrireDansAdresse("projet", ref);
  };
  const [vueTableau, setVueTableau] = createSignal(false);

  const analyse: Analyse = {
    get portefeuille() {
      return portefeuille();
    },
    get similarites() {
      return similarites();
    },
    get ordres() {
      return ordres();
    },
    aujourdhui,
    get selection() {
      return selection();
    },
    get recherche() {
      return recherche();
    },
    get misEnAvant() {
      return misEnAvant();
    },
    selectionner,
    allerA,
    filtrerSur: (s) => changerFiltres({ ...filtres(), ...s }),
  };

  // --- compteurs ---------------------------------------------------------------
  // Tous calculés, aucun écrit (R2).
  const chiffres = createMemo(() => {
    // « Groupes » et non « familles » : le mot est pris par les familles de leviers.
    const groupes = grappes(projets().length, liens()).filter((g) => g.length > 1);
    const relies = groupes.reduce((t, g) => t + g.length, 0);
    return [
      { intitule: "projets", valeur: projets().length },
      { intitule: "rapprochements", valeur: liens().length },
      { intitule: "groupes", valeur: groupes.length },
      { intitule: "sans équivalent", valeur: projets().length - relies },
    ];
  });

  // --- alerte et export --------------------------------------------------------
  const [alerte, setAlerte] = createSignal<string | null>(null);
  let minuteurAlerte: ReturnType<typeof setTimeout> | undefined;
  const signaler = (message: string) => {
    setAlerte(message);
    clearTimeout(minuteurAlerte);
    minuteurAlerte = setTimeout(() => setAlerte(null), 9000);
  };
  onCleanup(() => clearTimeout(minuteurAlerte));
  const { figures, Fournisseur: FournisseurFigures } = creerRegistreFigures();
  const [outilsPropres, setOutilsPropres] = createSignal<HTMLElement>();
  let graphe: { recadrer: () => void } | undefined;

  const onglets = () => ongletsPour(vue());
  // Sur téléphone, les onglets défilent : l'onglet ouvert est ramené à l'écran.
  createEffect(() => {
    const id = vue();
    nav
      ?.querySelector<HTMLElement>(`#onglet-${id}`)
      ?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  });
  const ongletsDe = (droite: boolean) => onglets().filter((v) => v.aDroite === droite);
  let nav: HTMLElement | undefined;
  // Flèches gauche/droite entre onglets (motif ARIA « tabs »).
  const naviguer = (e: KeyboardEvent, id: IdVue) => {
    const pas = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!pas) return;
    e.preventDefault();
    const liste = onglets();
    const i = liste.findIndex((v) => v.id === id);
    const suivante = liste[(i + pas + liste.length) % liste.length];
    if (!suivante) return;
    allerA(suivante.id);
    nav?.querySelector<HTMLElement>(`#onglet-${suivante.id}`)?.focus();
  };

  const onglet = (v: (typeof VUES)[number]) => (
    <button
      type="button"
      role="tab"
      id={`onglet-${v.id}`}
      class="onglet"
      classList={{ actif: vue() === v.id }}
      aria-selected={vue() === v.id}
      aria-controls="contenu"
      tabIndex={vue() === v.id ? 0 : -1}
      onClick={() => allerA(v.id)}
      onKeyDown={(e) => naviguer(e, v.id)}
    >
      <Icone nom={v.icone} />
      <span class="onglet-libelle" data-libelle={v.libelle}>
        {v.libelle}
      </span>
    </button>
  );

  return (
    <FournisseurFigures>
      <Entete surAccueil={() => allerA("accueil")}>
        <nav ref={(e) => (nav = e)} class="nav-onglets" role="tablist" aria-label="Vues">
          <div class="onglets">
            <For each={ongletsDe(false)}>{onglet}</For>
          </div>
          <div class="onglets onglets-droite">
            <For each={ongletsDe(true)}>{onglet}</For>
          </div>
        </nav>
      </Entete>

      <Show when={alerte()}>
        <div class="alerte" role="status">
          {alerte()}
        </div>
      </Show>

      {/* « Vous ne voyez pas tout » : un filtre oublié fait dire un chiffre faux. */}
      {/* Sur une vue de la donnée, la barre dit déjà combien de projets les filtres gardent. */}
      <Show when={!tousInclus() || (nbFiltres() && !surDonnee(vue()))}>
        <div class="bandeau" role="status">
          <span class="bandeau-texte">
            {projets().length} projets affichés sur {props.portefeuille.projets.length}
            {tousInclus()
              ? ""
              : `, exclus du calcul : ${presents()
                  .filter((e) => !etatsInclus().has(e.etat))
                  .map((e) => LIBELLES_ETAT_FILTRE[e.etat])
                  .join(", ")}`}
            {nbFiltres() ? ` · ${pluriel(nbFiltres(), "filtre")}` : ""}
          </span>
          <button
            type="button"
            class="lien-projet"
            onClick={() => changerPerimetre(new Set(TOUS_LES_ETATS), {})}
          >
            Tout réafficher
          </button>
        </div>
      </Show>

      <Show when={surDonnee(vue())}>
        <BarreCommandes
          recherche={recherche()}
          trouves={misEnAvant()?.size ?? null}
          surRecherche={changerRecherche}
          nbFiltres={nbFiltres()}
          voletOuvert={voletFiltres()}
          surVolet={setVoletFiltres}
          surErreur={signaler}
          lectureFiltres={
            nbFiltres()
              ? `${pluriel(projets().length, "projet")} sur ${props.portefeuille.projets.length}`
              : ""
          }
          volet={
            <VoletFiltres
              facettes={facettes()}
              index={index()}
              selection={filtres()}
              communs={communs()}
              nombreRetenus={retenus().length}
              libelleFacette={libelleFacette}
              teinte={(id, cle) =>
                id === "classe" ? couleurClasse(props.portefeuille, cle) : undefined
              }
              surBascule={basculerFiltre}
              surMode={changerModeFiltre}
              surEffacer={() => changerFiltres({})}
            />
          }
        />
      </Show>

      <main
        id="contenu"
        class="principal"
        tabIndex={-1}
        role={vue() === "accueil" ? undefined : "tabpanel"}
        aria-labelledby={vue() === "accueil" ? undefined : `onglet-${vue()}`}
      >
        <section class="carte-graphe" classList={{ "carte-texte": !surDonnee(vue()) }}>
          {/* Les outils qui agissent sur la figure sont posés sur elle, pas dans la barre. */}
          <Show when={surDonnee(vue())}>
            <div class="outils-vue">
              {/* Une vue y dépose ses propres outils (Portal), devant ceux communs. */}
              <div class="outils-propres" ref={setOutilsPropres} />
              <Show when={vue() === "graphe" && !vueTableau()}>
                <button
                  type="button"
                  class="bouton bouton-outil"
                  onClick={() => graphe?.recadrer()}
                >
                  <Icone nom="recadrer" />
                  Recadrer
                </button>
              </Show>
              <Show when={figures().length}>
                <MenuExport figures={figures()} surErreur={signaler} />
              </Show>
            </div>
          </Show>
          <Show when={vue() === "graphe"}>
            <TeteGraphe
              seuil={seuil()}
              plage={plage()}
              nombreProjets={projets().length}
              compterLiens={(s) => aretes(similarites(), s).length}
              surSeuil={changerSeuil}
              tableau={vueTableau()}
              surTableau={setVueTableau}
            />
          </Show>
          <Switch>
            <Match when={vue() === "graphe" && vueTableau()}>
              <div class="vue-cadre">
                <TableauRapprochements analyse={analyse} liens={liens()} seuil={seuil()} />
              </div>
            </Match>
            <Match when={vue() === "graphe"}>
              <GrapheSimilarite
                portefeuille={portefeuille()}
                similarites={similarites()}
                selection={selection()}
                misEnAvant={misEnAvant()}
                surSelection={selectionner}
                seuil={seuil()}
                placement={placement()}
                controle={(c) => (graphe = c)}
              />
            </Match>
            <Match when={vue() === "leviers"}>
              <div class="vue-cadre">
                <VueLeviers analyse={analyse} tri={tri()} surTri={changerTri} />
              </div>
            </Match>
            <Match when={vue() === "arbres"}>
              <VueArbres analyse={analyse} outils={outilsPropres()} />
            </Match>
            <Match when={vue() === "autres"}>
              {/* Le fourre-tout : ce qui n'a pas d'onglet à soi, une figure sous l'autre. */}
              <div class="vue-cadre">
                <VueChronologie analyse={analyse} />
                <VueCroisements analyse={analyse} />
                <VueCouverture analyse={analyse} />
                <VueExploration analyse={analyse} />
                <VueSimilarite analyse={analyse} />
              </div>
            </Match>
            <Match when={vue() === "accueil"}>
              <VueAccueil
                chiffres={chiffres()}
                portefeuille={portefeuille()}
                similarites={similarites()}
                allerA={allerA}
              />
            </Match>
            <Match when={vue() === "parametres"}>
              <VueParametres
                portefeuille={props.portefeuille}
                portefeuilleActif={portefeuille()}
                similarites={similarites()}
                aujourdhui={aujourdhui}
                poidsBruts={poidsBruts()}
                surPoids={changerPoids}
                etatsInclus={etatsInclus()}
                surEtats={changerEtats}
                placement={placement()}
                surPlacement={changerPlacement}
                surReinitialisation={reinitialiserModele}
              />
            </Match>
            <Match when={vue() === "avis"}>
              <VueAvis />
            </Match>
          </Switch>
          <Show when={surDonnee(vue())}>
            <Legende portefeuille={portefeuille()} />
          </Show>
        </section>
        <Show when={surDonnee(vue())}>
          <Panneau analyse={analyse} />
        </Show>
      </main>

      {/* Un colophon, pas une commande : de quoi relire une capture six mois plus tard. */}
      <footer class="pied">
        <span class="pied-droite">
          <span>
            Version {__VERSION_APPLI__} · {__CONSTRUITE_LE__}
          </span>
        </span>
      </footer>
    </FournisseurFigures>
  );
}

/**
 * L'adresse porte déjà toute la lecture (vue, filtres, projet, seuil…) : ce
 * bouton la met dans le presse-papiers, pour la partager telle quelle. Il agit
 * sur la page entière : sa place est dans la barre, pas sur une figure.
 */
function Partager(props: { surErreur: (message: string) => void }) {
  const [copie, setCopie] = createSignal(false);
  let minuteur: ReturnType<typeof setTimeout> | undefined;
  onCleanup(() => clearTimeout(minuteur));
  const copier = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopie(true);
      clearTimeout(minuteur);
      minuteur = setTimeout(() => setCopie(false), 2000);
    } catch {
      props.surErreur("Copie refusée par le navigateur : copiez l'adresse de la page à la main.");
    }
  };
  return (
    <button
      type="button"
      class="bouton bouton-menu"
      title="Copier le lien de cette vue, filtres compris"
      onClick={() => void copier()}
    >
      <Icone nom={copie() ? "coche" : "lien"} />
      {copie() ? "Lien copié" : "Partager"}
    </button>
  );
}

/**
 * Ce qui vaut pour toutes les vues de la donnée : la recherche met en avant
 * partout, les filtres retirent partout. Une seule ligne, de hauteur fixe : les
 * filtres actifs se lisent dans leur menu, pas ici.
 */
function BarreCommandes(props: {
  recherche: string;
  /** Projets trouvés, null sans recherche. */
  trouves: number | null;
  surRecherche: (q: string) => void;
  nbFiltres: number;
  voletOuvert: boolean;
  surVolet: (ouvert: boolean) => void;
  /** « 12 sur 85 » quand des filtres sont actifs, vide sinon. */
  lectureFiltres: string;
  surErreur: (message: string) => void;
  /** Le menu des facettes, déroulé sous le bouton Filtres. */
  volet: JSX.Element;
}) {
  let champ: HTMLInputElement | undefined;
  let menu: HTMLDivElement | undefined;
  let bouton: HTMLButtonElement | undefined;

  onMount(() => {
    // Le chemin plutôt que `contains` : cocher une valeur redessine la liste, et la
    // case cliquée n'est déjà plus dans le menu quand le clic arrive ici.
    const fermerDehors = (e: MouseEvent) => {
      if (menu && !e.composedPath().includes(menu)) props.surVolet(false);
    };
    const fermerEchap = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !props.voletOuvert) return;
      props.surVolet(false);
      bouton?.focus();
    };
    document.addEventListener("click", fermerDehors);
    document.addEventListener("keydown", fermerEchap);
    onCleanup(() => {
      document.removeEventListener("click", fermerDehors);
      document.removeEventListener("keydown", fermerEchap);
    });
  });
  return (
    <div class="barre">
      <div class="commande commande-recherche">
        <span class="champ-recherche">
          <Icone nom="loupe" />
          <input
            ref={(e) => (champ = e)}
            id="recherche"
            type="search"
            aria-label="Chercher un projet"
            placeholder="Chercher un projet…"
            value={props.recherche}
            onInput={(e) => props.surRecherche(e.currentTarget.value)}
          />
          {/* Notre croix, pareille dans tous les navigateurs (Firefox n'en a pas). */}
          <Show when={props.recherche}>
            <button
              type="button"
              class="effacer-recherche"
              title="Effacer la recherche"
              aria-label="Effacer la recherche"
              onClick={() => {
                props.surRecherche("");
                champ?.focus();
              }}
            >
              ×
            </button>
          </Show>
        </span>
        <span class="lecture" role="status">
          {props.trouves === null
            ? ""
            : `${props.trouves} ${props.trouves > 1 ? "trouvés" : "trouvé"}`}
        </span>
      </div>
      <div class="commande commande-filtres">
        <div class="menu" ref={(e) => (menu = e)}>
          <button
            ref={(e) => (bouton = e)}
            type="button"
            class="bouton bouton-menu"
            classList={{ actif: props.voletOuvert }}
            aria-haspopup="true"
            aria-expanded={props.voletOuvert}
            aria-controls="volet-filtres"
            onClick={() => props.surVolet(!props.voletOuvert)}
          >
            <Icone nom="filtre" />
            Filtres
            <Show when={props.nbFiltres}>
              <span class="facette-compte">{props.nbFiltres}</span>
            </Show>
            <span class="chevron">▾</span>
          </button>
          <Show when={props.voletOuvert}>{props.volet}</Show>
        </div>
        <span class="lecture" role="status">
          {props.lectureFiltres}
        </span>
      </div>
      <div class="commande commande-partage">
        <Partager surErreur={props.surErreur} />
      </div>
    </div>
  );
}

/**
 * Les réglages du graphe, posés dans sa zone : Graphe / Liste et le seuil, qui
 * vaut pour les deux affichages.
 */
function TeteGraphe(props: {
  seuil: number;
  plage: { min: number; max: number };
  nombreProjets: number;
  compterLiens: (seuil: number) => number;
  surSeuil: (seuil: number) => void;
  tableau: boolean;
  surTableau: (tableau: boolean) => void;
}) {
  // Le graphe se reconstruit au relâchement : replacer tous les nœuds à chaque
  // pixel de glissement rendrait le curseur poisseux. La lecture, elle, suit.
  const [provisoire, setProvisoire] = createSignal<number | null>(null);
  const affiche = () => provisoire() ?? props.seuil;
  /** Part du rail colorée, de la gauche à la poignée. */
  const remplissage = () => {
    const etendue = props.plage.max - props.plage.min;
    return `${etendue > 0 ? ((affiche() - props.plage.min) / etendue) * 100 : 0}%`;
  };
  const lecture = () => {
    const n = props.compterLiens(affiche());
    const parProjet = props.nombreProjets ? n / props.nombreProjets : 0;
    return `${formaterScore(affiche())} · ${pluriel(n, "lien")} · ${parProjet.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} par projet`;
  };

  return (
    <div class="tete-vue">
      <div class="onglets onglets-petits" role="group" aria-label="Affichage">
        <button
          type="button"
          class="onglet"
          classList={{ actif: !props.tableau }}
          aria-pressed={!props.tableau}
          onClick={() => props.surTableau(false)}
        >
          <Icone nom="reseau" />
          Graphe
        </button>
        <button
          type="button"
          class="onglet"
          classList={{ actif: props.tableau }}
          aria-pressed={props.tableau}
          onClick={() => props.surTableau(true)}
        >
          <Icone nom="liste" />
          Liste
        </button>
      </div>
      <div class="commande commande-seuil">
        <label class="intitule" for="seuil">
          Seuil
        </label>
        <input
          id="seuil"
          type="range"
          min={props.plage.min}
          max={props.plage.max}
          step="0.01"
          value={affiche()}
          style={{ "--rempli": remplissage() }}
          aria-describedby="lecture-seuil"
          onInput={(e) => setProvisoire(Number(e.currentTarget.value))}
          onChange={(e) => {
            // Lu avant de lâcher la valeur provisoire : sinon le champ est déjà revenu
            // à l'ancien seuil.
            const valeur = Number(e.currentTarget.value);
            setProvisoire(null);
            props.surSeuil(valeur);
          }}
        />
        <output id="lecture-seuil" class="lecture" for="seuil">
          {lecture()}
        </output>
      </div>
    </div>
  );
}
